import { Node } from './node';
import { Edge } from './edge';
import { Grid } from './grid';
import { GraphNode } from './graph-node';
import { ColorTreeNode } from '../../Tree/colorTree/color-tree-node';

/**
 * The abstract class Graph describes the logical representation of a graph.
 * It manages the data structures of the nodes and edges. 
 * Modifications made to the graph are handled by specific methods
 * that ensure the consistency of the graph. 
 */
export abstract class Graph {
    protected ctx: CanvasRenderingContext2D; //Our drawing context
    protected canvas: HTMLCanvasElement; //The HTMLCanvas we're referencing to
    protected grid: Grid; //Graph grid
    protected nodes: Node[] = []; //Reference to the nodes on the graph
    protected edges: Edge[] = []; //Reference to the edges on the graph
    protected disabled: boolean = false; //Flag to disable any interaction with the graph [Not consistent]

    protected readonly rows: number; //Number of rows on the graph
    protected readonly cols: number; //Number of cols on the graph
    protected resizable: boolean = false; //Dynamic resizing of the graph

    //Names of nodes if not specified otherly
    private mockNodeNames: string[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q'
        , 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

    /**
     * 
     * @param canvas , reference to the canvas on the HTML
     * @param rows , number of rows for the grid
     * @param cols , number of columns for the grid
     */
    constructor(canvas: HTMLCanvasElement, rows: number, cols: number) {
        this.canvas = canvas;
        this.rows = rows;
        this.cols = cols;

        this.ctx = this.canvas.getContext('2d');
        this.grid = new Grid(this.rows, this.cols, canvas);

        this.canvas.addEventListener('mousedown', this.mouseClick, false);
        this.canvas.addEventListener('dblclick', this.doubleMouseClick, false);
        window.addEventListener('resize', this.resize, false);
        window.addEventListener('keydown', this.keyboardClick, false);
    }
    
    /**
     * 
     * @param row, row coordinate on the grid
     * @param col, col coordinate on the grid
     * @param node, node on coordinate [row,col]
     * @param e Mousevent specification
     */
    protected abstract leftClick(row: number, col: number, node: Node, e: MouseEvent);
    
    /**
     * 
     * @param row, row coordinate on the grid
     * @param col, col coordinate on the grid
     * @param node, node on coordinate [row,col]
     * @param e Mousevent specification
     */
    protected abstract rightClick(row: number, col: number, node: Node, e: MouseEvent);
    
    /**
     * 
     * @param row, row coordinate on the grid
     * @param col, col coordinate on the grid
     * @param node, node on coordinate [row,col]
     * @param e Mousevent specification
     */
    protected abstract doubleClick(row: number, col: number, node: Node, e: MouseEvent);

    /**
     * @param startNode, outgoing node of the edge
     * @param endNode, incoming node of the edge
     * 
     * Deletes the corresponding edge.
     */
    protected abstract deleteEdge(startNode: Node, endNode: Node);

    
    /**
     * @param startNode, outgoing node of the edge
     * @param endNode, incoming node of the edge
     * 
     * Returns true, if an edge exists from startNode to endNode.
     */
    protected abstract existsEdge(startNode: Node, endNode: Node):boolean;
    /**
     * 
     * @param e Keyboardevent from the user
     * Captures any keyboard event made by the user.
     */
    protected abstract keyboardEvent(e: KeyboardEvent);

    /**
     * 
     * @param fromNode, outgoing node of the edge
     * @param toNode, incoming node of the edge
     * returns the corresponding edge if it exists, else returns null
     */
    public abstract getEdge(startNode: Node, endNode: Node): Edge;
  
    /**
     * 
     * @param startNode, outgoing node of the edge
     * @param endNode, incoming node of the edge
     * Creates an edge from the startNode to the endNode
     */
    abstract addEdge(startNode: Node, endNode: Node);

    /**
     * updating the graph
     */
    update() {
        if (!this.disabled)
            this.draw();
    }

    setDisabled(disabled : boolean){
        this.disabled = disabled;
    }

    setGrid(grid: Grid) {
        this.grid = grid;
    }
    getGrid(): Grid {
        return this.grid;
    }

    getRows(): number {
        return this.rows;
    }

    getCols(): number {
        return this.cols;
    }

    getNodes(): Node[] {
        return this.nodes;
    }

    getEdges(): Edge[] {
        return this.edges;
    }

    /**
     * Enables the graph to dynamically change its size corresponding to the current size of the browser window
     */
    enableDynamicResize(enable: boolean) {
        this.resizable = enable;
    }
    protected resize = () => {
        this.grid.onResize();
        this.update();
    }

    /**
     * Set Nodes and Edges back to their original visual state.
     */
    setNodesAndEdgesToDefault() {
        this.nodes.forEach(node => {
            node.setDefaultColor();
        })

        this.edges.forEach(edge => {
            edge.setToDefault();
        })
    }

    /**
     * Resets the graph by emptying the graph.
     */
    resetGraph() {
        this.nodes = [];
        this.edges = [];
        this.update();
    }
    /**
     * Draws the graph, no need to call it explicitly, rather call update() method.
     */
    protected draw() {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        //  this.ctx.fillStyle = 'rgb(255,254,236)';
        //  this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        this.edges.forEach(edge => {
            edge.draw(this.ctx, this.grid);
        });

        this.nodes.forEach(node => {
            node.draw(this.ctx, this.grid);
        });

        this.edges.forEach(edge => {
            edge.drawBezierPoint(this.ctx, this.grid);
        })

        this.grid.draw(this.ctx);
    }

    protected keyboardClick = (e: KeyboardEvent) => {
        this.keyboardEvent(e);
    }

    protected doubleMouseClick = (e: MouseEvent) => {
        var coord: [number, number] = this.convert(e.offsetX, e.offsetY);
        var row: number = coord[0];
        var col: number = coord[1];

        var node: Node = this.getNode(row, col);
        this.doubleClick(row, col, node, e);
        this.update();
    }
    /**
     * Handles the following event: left mouse-click. Can be overriden to define customized handling event
     */
    protected mouseClick = (e: MouseEvent) => {
        var coord: [number, number] = this.convert(e.offsetX, e.offsetY);
        var row: number = coord[0];
        var col: number = coord[1];

        var node: Node = this.getNode(row, col);

        //Left mouse click, e.button == 0
        if (e.button == 0) {
            this.leftClick(row, col, node, e);
        }
        //Right click
        else if (e.button == 2) {
            this.rightClick(row, col, node, e);
        }

        this.update();
    }


    /**
     * 
     * @param row , row index of the searched node on the grid
     * @param col , column index of the searched node on the grid
     * @returns node, if node exists on the grid at this coordinate. null, if no node exists at this coordinate
     */
    getNode(row: number, col: number): Node {
        var searchedNode: Node = null;
        this.nodes.forEach(node => {
            if (node.getColIndex() == col && node.getRowIndex() == row) {
                searchedNode = node;
            }
        });

        return searchedNode;
    }

    /**
    * 
    * @param id, id of the node.
    * We return the node with the corresponding id. If it does not exist we return null. 
    */
    getNodePerID(id: number) {
        var searchedNode: Node = null;
        this.nodes.forEach(node => {
            if (node.getId() == id) {
                searchedNode = node;
            }
        });

        return searchedNode;
    }

    /**
     * @param delNode: Reference to the node that should be deleted
     * Delete a node from the graph.
     */
    protected deleteNode(delNode: Node) {
        //We need to do this workaround (creating a stack of edges to delete instead of deleting directly of the edges array)
        //Else we would be iterating an array where elements get concurrently removed (which breaks the loop after one iteration)
        var edgesToDelete: Edge[] = [];
        this.edges.forEach(edge => {
            if (edge.getFromNode() == delNode || edge.getToNode() == delNode) {
                edgesToDelete.push(edge);
            }
        });

        edgesToDelete.forEach(edge => {
            this.deleteEdge(edge.getFromNode(), edge.getToNode());
        })

        const index = this.nodes.indexOf(delNode, 0);

        if (index > -1) {
            this.nodes.splice(index, 1);
        }
        this.update();
    }
    /**
     * converts the raw x and y coordinate to indices of the grid
     */
    protected convert(xRaw: number, yRaw: number): [number, number] {
        var coordinates: [number, number] = this.grid.getCellCoordinates(xRaw, yRaw);
        return coordinates;
    }

    //To delete, doesn't belong to a standard graph implementation
    addNode(row: number, col: number, id: number, name?: string): Node{
        //Test that theres no node at this coordinate
        var nodeExistsThere: boolean = false;

        this.nodes.forEach(node => {
            if (node.getColIndex() == col && node.getRowIndex() == row) {
                nodeExistsThere = true;
                return;
            }
        });

        if (nodeExistsThere)
            return;

        let newNode: Node = new GraphNode(row, col, id);

        let newName: string = null;

        if (name == null) {
            let i = 0;
            let j = 0;

            for (i = 0; i < this.mockNodeNames.length; i++) {
                let nameExists = false;
                for (j = 0; j < this.nodes.length; j++) {
                    if (this.nodes[j].getName() == this.mockNodeNames[i]) {
                        nameExists = true;
                        break;
                    }
                }
                if (!nameExists) {
                    newName = this.mockNodeNames[i];
                    break;
                }


                if (newName == null) {
                    newName = "undefined";
                }
            }
        }
        else {
            newName = name;
        }

        newNode.setName(newName);
        this.nodes.push(newNode);
        return newNode;
    }

    /**
     * 
     * @param node node to be added, which has been previously removed
     */
    addExistentNode(node: Node) {
        this.nodes.push(node);
    }

    /**
     * 
     * @param visible, boolean, sets the visibility of the grid
     */
    protected setGridVisible(visible: boolean) {
        this.grid.setVisible(visible);
        this.update();
    }

}




